import { FC, useEffect, useState } from "react"
import {
    Button,
    Dialog,
    DialogDescription,
    DialogHeader,
    DialogActions,
    Grid,
    GridRow,
    Icon,
    Input,
    InputProps,
    useIntl,
    DialogContent,
} from "@metsooutotec/modes-react-components"
import { observer } from "mobx-react-lite"
import { validateDecimal, validateText } from "validators/inputValidators"
import { ModalTypes } from "../../customers/utils/customer-modal-helper"
import customerStore, { CustomerStoreResults } from "features/organizations/customers/store/customer-store"
import { handleUploadFileClick, NavigationModalOptions, resetRecordFormData } from "components/utils/modal-helper"
import { Asset, CreateAsset, EditAsset } from "../models/asset-models"

interface AssetModalProps {
    isOpen: boolean | undefined
    onClose: (passed: boolean, customerId?: string, navigationAssetOpetions?: NavigationModalOptions) => void
    asset?: Asset
    siteId?: string
    customerId?: string
}

const AssetModal: FC<AssetModalProps> = observer(({ isOpen, onClose, asset, siteId, customerId }) => {
    const { formatMessage } = useIntl()
    const isEditing = !!asset?.id

    const [formData, setFormData] = useState<Record<string, string>>({
        assetName: "",
        assetDescription: "",
        wearThreshold: "",
        uploadImageName: "",
        upload3DFileName: "",
    })
    const [formErrors, setFormErrors] = useState<Record<string, string>>({})
    const [selectedImage, setSelectedImage] = useState<File | null>(null)
    const [selected3DFile, setSelected3DFile] = useState<File | null>(null)
    const [isLoading, setLoading] = useState(false)
    const [errorMessage, setErrorMessage] = useState("")

    useEffect(() => {
        if (!asset) return resetRecordFormData(setFormData)

        setFormData({
            assetName: asset.name || "",
            assetDescription: asset.description || "",
            wearThreshold: asset.wearThreshold?.toString() || "",
            uploadImageName: selectedImage?.name || "",
            upload3DFileName: selected3DFile?.name || "",
        })
    }, [asset])

    const handleChange = (id: string, value: string) => {
        setFormData(prev => ({ ...prev, [id]: value }))
        setErrorMessage("")
    }

    const handleValidation = (id: string, message: string) => {
        setFormErrors(prev => ({ ...prev, [id]: message }))
    }

    const handleClear = (id: string) => {
        setFormData(prev => ({ ...prev, [id]: "" }))
        setFormErrors(prev => ({ ...prev, [id]: "" }))
    }

    const handleSubmit = async () => {
        setLoading(true)
        const errors = {
            assetName: validateText(formData.assetName),
            assetDescription: validateText(formData.assetDescription),
            wearThreshold: validateDecimal(formData.wearThreshold),
        }
        setFormErrors(errors)

        if (Object.values(errors).some(msg => msg)) {
            setErrorMessage("Please fix the validation errors.")
            setLoading(false)
            return
        }

        const payload: CreateAsset = {
            customerId: customerId ?? "",
            siteId: siteId ?? "",
            name: formData.assetName,
            description: formData.assetDescription,
            wearThreshold: parseFloat(formData.wearThreshold),
            assetImage: selectedImage || new File([], "empty.jpg"),
            asset3DFile: selected3DFile || new File([], "empty.obj"),
        }

        const result: CustomerStoreResults = await (isEditing
            ? customerStore.editEntity({ ...payload, id: asset!.id } as EditAsset, ModalTypes.AssetModal)
            : customerStore.createEntity(payload, ModalTypes.AssetModal))

        if (!result.passed) {
            setErrorMessage(result.message)
            setLoading(false)
            return
        }

        onClose(true, result.entityId, { type: isEditing ? "edited" : "created" })
        setLoading(false)
    }

    const handleClose = () => {
        resetRecordFormData(setFormData)
        setSelectedImage(null)
        setSelected3DFile(null)
        setErrorMessage("")
        onClose(false)
    }

    const labels = {
        assetName: formatMessage("assetModal.name"),
        assetDescription: formatMessage("asset.description"),
        wearThreshold: formatMessage("asset.wearThreshold"),
        uploadImage: formatMessage("assetModal.uploadImage"),
        upload3DFile: formatMessage("assetModal.upload3DFile"),
        confirm: formatMessage("modal.confirm"),
        cancel: formatMessage("modal.cancel"),
        header: formatMessage(isEditing ? "assetModal.headerEdit" : "assetModal.header"),
        description: formatMessage("modal.description"),
    }

    const fields = [
        { id: "assetName", label: labels.assetName, validate: validateText },
        { id: "assetDescription", label: labels.assetDescription, validate: validateText },
        { id: "wearThreshold", label: labels.wearThreshold, validate: validateDecimal },
    ]

    const fileFields = [
        {
            id: "uploadImageName",
            label: labels.uploadImage,
            onUpload: () => handleUploadFileClick(".jpg", setSelectedImage, setFormData, null, "uploadImageName"),
            clear: () => {
                setSelectedImage(null)
                handleClear("uploadImageName")
            },
        },
        {
            id: "upload3DFileName",
            label: labels.upload3DFile,
            onUpload: () => handleUploadFileClick(".obj", setSelected3DFile, setFormData, null, "upload3DFileName"),
            clear: () => {
                setSelected3DFile(null)
                handleClear("upload3DFileName")
            },
        },
    ]

    return (
        <Dialog open={isOpen} size='small' dimmer style={{ maxWidth: "512px" }}>
            <DialogHeader>{labels.header}</DialogHeader>
            {errorMessage && <p style={{ color: "red", margin: "0 1rem" }}>{errorMessage}</p>}
            <DialogDescription>{labels.description}</DialogDescription>
            <DialogContent>
                <Grid>
                    {fields.map(({ id, label, validate }) => (
                        <GridRow key={id}>
                            <Input
                                id={id}
                                label={label}
                                placeholder={`Type ${label}...`}
                                value={formData[id]}
                                error={formErrors[id]}
                                validate={validate}
                                onValidation={msg => handleValidation(id, msg as string)}
                                onChange={e => handleChange(id, e.target.value)}
                                icon={formData[id] && <Icon className='icon-cross-circle-fill-16' onClick={() => handleClear(id)} />}
                                spanning
                                required
                                uiRequired
                                heightClass='medium'
                            />
                        </GridRow>
                    ))}

                    {fileFields.map(({ id, label, onUpload, clear }) => (
                        <GridRow key={id}>
                            <Input
                                id={id}
                                label={label}
                                value={formData[id]}
                                placeholder='Select a file...'
                                icon={
                                    <>
                                        {formData[id] && <Icon className='icon-cross-circle-fill-16' onClick={clear} />}
                                        <Icon className='icon-upload-16' onClick={onUpload} />
                                    </>
                                }
                                onClick={onUpload}
                                readOnly
                                spanning
                                heightClass='medium'
                            />
                        </GridRow>
                    ))}
                </Grid>
            </DialogContent>

            <DialogActions>
                <Button children={labels.cancel} onClick={handleClose} flexible centered size='medium' />
                <Button
                    children={labels.confirm}
                    onClick={handleSubmit}
                    primary
                    flexible
                    centered
                    size='medium'
                    loading={isLoading}
                    disabled={isLoading}
                />
            </DialogActions>
        </Dialog>
    )
})

export default AssetModal
